import { apiConfig } from '@/shared/api/config';

import { dpopKeyManager } from './dpopKeyManager';
import { createDpopProof } from './dpopProofFactory';
import { extractJktFromToken, stripBearer } from './dpopUtils';

/**
 * URL path segments that completely skip DPoP.
 * These are PIN-related endpoints that operate in a separate security domain.
 */
const SKIP_DPOP_PATH_SEGMENTS = ['/pin/'] as const;

/**
 * URL path segments that are "token endpoints" per RFC 9449 §5.
 * DPoP proof is added but ath MUST NOT be included.
 */
const TOKEN_ENDPOINT_PATH_SEGMENTS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/register',
  '/auth/google/',
  '/auth/legacy',
  '/auth/verify-email',
  '/auth/request-email-verification',
] as const;

const getPathname = (url: string): string => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

const isSkipDpopUrl = (pathname: string): boolean => {
  return SKIP_DPOP_PATH_SEGMENTS.some((seg) => pathname.includes(seg));
};

const isTokenEndpointUrl = (pathname: string): boolean => {
  return TOKEN_ENDPOINT_PATH_SEGMENTS.some((seg) => pathname.includes(seg));
};

/**
 * Extract the raw access token string from the Authorization header.
 * Returns null if the header is absent or is not a Bearer token.
 */
const extractAccessTokenFromHeaders = (headers: Headers): string | null => {
  const authorization = headers.get('authorization') ?? headers.get('Authorization');
  if (!authorization?.toLowerCase().startsWith('bearer ')) return null;
  return stripBearer(authorization);
};

/**
 * Creates a fetch wrapper that injects DPoP proofs into outgoing requests.
 *
 * Injection rules:
 * 1. /pin/** paths     → passthrough, no DPoP header
 * 2. Token endpoints   → auto-generate key if absent, then add DPoP header, NO ath
 * 3. All other paths   → add DPoP header if key is loaded; ath added only if
 *    the Bearer token is DPoP-bound (cnf.jkt present)
 */
export const createDpopFetch = (): typeof fetch => {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method ?? 'GET';
    const pathname = getPathname(url);

    // Rule 1: PIN endpoints — skip entirely
    if (isSkipDpopUrl(pathname)) {
      return fetch(input, init);
    }

    const isTokenEndpoint = isTokenEndpointUrl(pathname);

    // Auto-generate a key before the first token endpoint call (/login) so
    // the backend can derive jkt from the DPoP proof and bind it to the session.
    if (isTokenEndpoint && !dpopKeyManager.hasKey()) {
      try {
        await dpopKeyManager.prepareForLogin();
      } catch (error) {
        console.error('[DPoP] Failed to prepare key for login:', error);
        return fetch(input, init);
      }
    }

    // No key — resource endpoint before any login, skip DPoP
    if (!dpopKeyManager.hasKey()) {
      return fetch(input, init);
    }

    const headers = new Headers(init?.headers);

    let accessTokenForAth: string | undefined;

    if (!isTokenEndpoint) {
      const rawToken = extractAccessTokenFromHeaders(headers);
      if (rawToken) {
        const jkt = extractJktFromToken(rawToken);
        // Only add ath if this token is DPoP-bound (cnf.jkt present)
        if (jkt) {
          accessTokenForAth = rawToken;
        }
      }
    }

    // Use the canonical gateway origin for htu so it always matches what the backend
    // expects, regardless of dev proxies.
    const htuUrl = apiConfig.gtwUrl + pathname;

    try {
      const proof = await createDpopProof({
        method,
        url: htuUrl,
        accessToken: accessTokenForAth,
      });
      headers.set('DPoP', proof);
    } catch (error) {
      // If key is missing or signing fails, proceed without DPoP rather than
      // breaking the request. The backend will reject if DPoP is required.
      console.error('[DPoP] Failed to create proof:', error);
      return fetch(input, init);
    }

    return fetch(input, { ...init, headers });
  };
};

// Singleton DPoP-aware fetch — inject as customFetch into HttpClient
export const dpopFetch = createDpopFetch();
